import PropTypes from 'prop-types';
import { size, map } from '@/helpers/lodash';
import EpisodePreviewSmall from '@/components/app/EpisodePreviews/EpisodePreviewSmall';
import SortableList from '@/components/app/SortableList';
import { classes } from '@/helpers/styling';
import { isEqualAsNumber } from '@/helpers/math';
import { isTouchDevice } from '@/helpers/browser';

const EpisodePreviewSmallList = ({
  episodes,
  episodeKeys,
  isSortable,
  onMoveEpisode,
  currentlyPlayingEpisodeId,
  scrollToEpisodeNumber,
  scrollToSeasonNumber,
  listClassName,
  episodeClassName,
  getEpisodeProps,
  loading,
  loadingCount,
  episodeKeyPrefix,
  loadingMore,
  loadingMoreCount
}) => {
  const episodesToDisplay = map(episodes, (episode, index) => {
    return {
      component: (
        <EpisodePreviewSmall
          episode={ episode }
          episodeKey={ episodeKeys[index] || episode.id }
          isPlaying={ currentlyPlayingEpisodeId === episode.id }
          { ...getEpisodeProps(episode) }
        />
      ),
      id: (
        isEqualAsNumber(scrollToEpisodeNumber, episode.episode_number)
        && isEqualAsNumber(scrollToSeasonNumber, episode.season)
      )
        ? 'scroll-to-episode'
        : null,
      key: `${ episodeKeyPrefix }${ episodeKeyPrefix ? '-' : '' }${ episode.id }`
    };
  });

  if (isSortable) {
    return (
      <SortableList
        listClassName='episode-preview-list-wrapper sortable'
        listItemClassName={ classes(episodeClassName, 'episode-preview-queue-wrapper') }
        listItems={ episodesToDisplay.map((e, index) => ({
          element: e.component,
          key: episodeKeys[index] || e.id
        })) }
        shouldCancelStart={ (e) => (e.target.getAttribute('data-type') === 'menu-button') }
        sortableContainerProps={ {
          onSortEnd: ({ oldIndex, newIndex }) => {
            onMoveEpisode({
              from: oldIndex,
              to: newIndex
            });
          },
          helperClass: 'episode-preview-sortable-helper',
          lockAxis: 'y',
          transitionDuration: 200,
          ...(isTouchDevice() ? { pressDelay: 400 } : { distance: 20 }),
          lockOffset: [0, 0]
        } }
      />
    );
  }

  return (
    <ul className={ classes('episode-preview-list-wrapper', listClassName) }>
      { size(episodesToDisplay) && !loading
        ? (
          <>
            { episodesToDisplay.map(({
              component, id, key
            }) => {
              return (
                <li className={ classes('episode-preview-cross-fade', episodeClassName) } id={ id } key={ key }>
                  { component }
                </li>
              );
            }) }
            { loadingMore && [...Array(loadingMoreCount)].map((_, i) => (
              <li className={ episodeClassName } key={ `loading-more-${ i }` }>
                <EpisodePreviewSmall { ...getEpisodeProps({}) } loading />
              </li>
            )) }
          </>
        ) : [...Array(loadingCount)].map((_, i) => (
          <li className={ episodeClassName } key={ `loading-${ i }` }>
            <EpisodePreviewSmall { ...getEpisodeProps({}) } loading />
          </li>
        )) }
    </ul>
  );
};

EpisodePreviewSmallList.propTypes = {
  episodes: PropTypes.array,
  isSortable: PropTypes.bool,
  episodeKeys: PropTypes.array,
  onMoveEpisode: PropTypes.func,
  currentlyPlayingEpisodeId: PropTypes.string,
  scrollToEpisodeNumber: PropTypes.number,
  scrollToSeasonNumber: PropTypes.number,
  episodeClassName: PropTypes.string,
  getEpisodeProps: PropTypes.func,
  loading: PropTypes.bool,
  listClassName: PropTypes.string,
  loadingCount: PropTypes.number,
  episodeKeyPrefix: PropTypes.string,
  loadingMore: PropTypes.bool,
  loadingMoreCount: PropTypes.bool
};

EpisodePreviewSmallList.defaultProps = {
  episodes: [],
  isSortable: false,
  episodeKeys: [],
  onMoveEpisode: undefined,
  currentlyPlayingEpisodeId: null,
  scrollToEpisodeNumber: null,
  scrollToSeasonNumber: null,
  episodeClassName: '',
  getEpisodeProps: () => {},
  loading: false,
  listClassName: '',
  loadingCount: 20,
  episodeKeyPrefix: '',
  loadingMore: false,
  loadingMoreCount: false
};

export default EpisodePreviewSmallList;